/**
 * Cliente REST do Google Drive — backup do Kontto na NUVEM DO USUÁRIO (ver ADR 0027).
 *
 * 100% desacoplado da lib de auth: cada função recebe um `token` (access token do
 * Google Sign-In). A camada de orquestração (driveBackup.ts) pega o token com refresh
 * silencioso e, em erro de auth (401), reconecta e tenta de novo. Assim este módulo é
 * `fetch` puro — sem módulo nativo, testável com fetch mockado.
 *
 * Escopo OAuth: `drive.file` (non-sensitive, sem CASA) — só enxergamos os arquivos que o
 * próprio app criou. Por isso a busca da pasta "Kontto" e a listagem só retornam o que é
 * nosso; um "Kontto" que o usuário criou na mão no Drive é invisível para nós (aceitável).
 */

const DRIVE_API = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD = 'https://www.googleapis.com/upload/drive/v3';
const FOLDER_MIME = 'application/vnd.google-apps.folder';

/** Nome (visível ao usuário) da pasta de backups no Drive dele. */
export const BACKUP_FOLDER_NAME = 'Kontto';
/** Prefixo dos arquivos de backup — usado pra filtrar a listagem. */
export const BACKUP_PREFIX = 'kontto_backup_';

export interface DriveFile {
    id: string;
    name: string;
    /** RFC 3339 (ex.: "2026-07-01T12:00:00.000Z"). */
    createdTime?: string;
    /** Tamanho em bytes, como string (padrão do Drive). Ausente em pastas. */
    size?: string;
}

/** Erro de chamada à Drive API. `isAuthError` distingue "token caiu" (reconectar) do resto. */
export class DriveApiError extends Error {
    status: number;
    isAuthError: boolean;
    isNotFoundError: boolean;

    constructor(message: string, status: number) {
        super(message);
        this.name = 'DriveApiError';
        this.status = status;
        // 401 = token expirado/revogado → reconectar. 403 pode ser cota/permissão;
        // não tratamos como auth (reconectar não resolve cota).
        this.isAuthError = status === 401;
        // 404 = alvo (ex.: a pasta salva) não existe mais → a camada de cima re-cria.
        this.isNotFoundError = status === 404;
    }
}

/**
 * O pedido nem chegou a ter resposta do Drive: sem internet, DNS, ou conexão pendurada até o teto
 * de 30s. Guarda o texto cru do `fetch` ("fetch failed: Fetch request has been canceled") para o
 * log; quem mostra ao usuário troca por uma frase (ver `errorMessage` em driveBackup.ts).
 */
export class DriveNetworkError extends Error {
    constructor(cause: unknown) {
        super(cause instanceof Error ? cause.message : String(cause));
        this.name = 'DriveNetworkError';
    }
}

/** Teto de tempo por chamada — uma conexão pendurada não pode travar o backup indefinidamente. */
const DRIVE_TIMEOUT_MS = 30_000;

async function driveFetch(token: string, url: string, init?: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), DRIVE_TIMEOUT_MS);
    let res: Response;
    try {
        res = await fetch(url, {
            ...init,
            signal: controller.signal,
            headers: {
                Authorization: `Bearer ${token}`,
                ...(init?.headers ?? {}),
            },
        });
    } catch (error) {
        // `fetch` só rejeita quando não há resposta; status de erro do Drive cai no `!res.ok` abaixo.
        throw new DriveNetworkError(error);
    } finally {
        clearTimeout(timeout);
    }

    if (!res.ok) {
        let detail = '';
        try {
            detail = await res.text();
        } catch {
            // corpo ilegível — segue com o status
        }
        if (res.status === 404) folderCache = null;
        throw new DriveApiError(
            `Drive API ${res.status}: ${detail.slice(0, 300) || res.statusText}`,
            res.status,
        );
    }

    return res;
}

/**
 * Acha a pasta "Kontto" do app (drive.file só vê a nossa) ou cria se não existir.
 * Retorna o folderId.
 *
 * Corrida entre dois aparelhos (ou dois fluxos do mesmo boot) criando ao mesmo tempo: o Drive
 * PERMITE pastas homônimas, então os dois podem criar e cada um passaria a escrever na sua —
 * backups numa, segmentos de sync noutra, sem nunca convergir. Mitigação: a busca ordena por
 * createdTime e todo mundo adota a MAIS ANTIGA; quem cria re-busca em seguida e, se perdeu a
 * corrida, abandona a própria pasta e converge pra do vencedor.
 */
// A pasta não muda de id: lembrar por alguns minutos poupa uma ida ao Drive em cada passo que
// precisa dela (conectar, chave, ciclo — no join da web eram quatro buscas iguais em sequência,
// cada uma uma viagem até o Google). Um 404 em qualquer chamada esquece o cache, caso a pasta
// tenha sido apagada no meio.
const FOLDER_CACHE_MS = 5 * 60 * 1000;
let folderCache: { token: string; id: string; at: number } | null = null;
export function forgetBackupFolder(): void {
    folderCache = null;
}

export async function ensureBackupFolder(token: string): Promise<string> {
    if (folderCache && folderCache.token === token && Date.now() - folderCache.at < FOLDER_CACHE_MS) {
        return folderCache.id;
    }
    const id = await lookupOrCreateBackupFolder(token);
    folderCache = { token, id, at: Date.now() };
    return id;
}

async function lookupOrCreateBackupFolder(token: string): Promise<string> {
    const q = encodeURIComponent(
        `name='${BACKUP_FOLDER_NAME}' and mimeType='${FOLDER_MIME}' and trashed=false`,
    );
    const search = async (): Promise<string | null> => {
        const res = await driveFetch(
            token,
            `${DRIVE_API}/files?q=${q}&spaces=drive&orderBy=createdTime&fields=files(id,name)&pageSize=10`,
        );
        const data = await res.json();
        return (data.files?.[0] as DriveFile | undefined)?.id ?? null;
    };

    const existing = await search();
    if (existing) return existing;

    const created = await driveFetch(token, `${DRIVE_API}/files?fields=id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: BACKUP_FOLDER_NAME, mimeType: FOLDER_MIME }),
    });
    const folder = await created.json();

    // Re-busca: se outro fluxo criou primeiro, a mais antiga vence (inclusive a nossa, se ganhou).
    return (await search()) ?? (folder.id as string);
}

/**
 * Sobe um backup (conteúdo cifrado — envelope .kontto) pra pasta, via multipart.
 *
 * Boundary fixo é seguro aqui porque o conteúdo cloud é SEMPRE o envelope cifrado
 * (chaves JSON fixas + ciphertext em hex 0-9a-f) — não há como conter a string do
 * boundary (diferente de um JSON aberto com descrição livre do usuário).
 */
export async function uploadBackup(
    token: string,
    folderId: string,
    filename: string,
    content: string,
): Promise<DriveFile> {
    const boundary = 'kontto_backup_boundary_x7f3a9';
    const metadata = { name: filename, parents: [folderId] };
    const body =
        `--${boundary}\r\n` +
        'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
        `${JSON.stringify(metadata)}\r\n` +
        `--${boundary}\r\n` +
        'Content-Type: application/json\r\n\r\n' +
        `${content}\r\n` +
        `--${boundary}--`;

    const res = await driveFetch(
        token,
        `${DRIVE_UPLOAD}/files?uploadType=multipart&fields=id,name,createdTime,size`,
        {
            method: 'POST',
            headers: { 'Content-Type': `multipart/related; boundary=${boundary}` },
            body,
        },
    );
    return res.json();
}

/**
 * Lista os arquivos da pasta com um dado prefixo, mais recentes primeiro.
 *
 * PAGINA até o fim (nextPageToken): com pageSize=100 e sem paginação, a pasta estourava a
 * janela em semanas de sync (um segmento novo por ciclo com mudanças) — aparelho atrasado
 * deixava de ver segmentos antigos (perda silenciosa) e até o `kontto_sync_meta.json` saía
 * da listagem, fazendo um salt novo ser criado (split-brain de chave). Teto de segurança em
 * 50 páginas (5.000 arquivos) só pra não laçar para sempre numa pasta patológica.
 */
export async function listByPrefix(token: string, folderId: string, prefix: string): Promise<DriveFile[]> {
    const q = encodeURIComponent(`'${folderId}' in parents and trashed=false`);
    const files: DriveFile[] = [];
    let pageToken: string | undefined;
    for (let page = 0; page < 50; page += 1) {
        const res = await driveFetch(
            token,
            `${DRIVE_API}/files?q=${q}&spaces=drive&orderBy=createdTime%20desc`
                + '&fields=nextPageToken,files(id,name,createdTime,size)&pageSize=100'
                + (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ''),
        );
        const data = await res.json();
        files.push(...((data.files ?? []) as DriveFile[]));
        pageToken = data.nextPageToken;
        if (!pageToken) break;
    }
    if (pageToken) console.warn('[drive] listagem truncada em 50 páginas — pasta grande demais');
    return files.filter((f) => f.name?.startsWith(prefix));
}

/** Sobrescreve o CONTEÚDO de um arquivo existente (mesmo fileId) — ex.: upgrade do sync meta. */
export async function updateFileContent(token: string, fileId: string, content: string): Promise<void> {
    await driveFetch(token, `${DRIVE_UPLOAD}/files/${fileId}?uploadType=media&fields=id`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: content,
    });
}

/** Lista os backups do Kontto na pasta, mais recentes primeiro. */
export async function listBackups(token: string, folderId: string): Promise<DriveFile[]> {
    return listByPrefix(token, folderId, BACKUP_PREFIX);
}

/** Baixa o conteúdo cru (texto) de um backup — pra restaurar. */
export async function downloadBackup(token: string, fileId: string): Promise<string> {
    const res = await driveFetch(token, `${DRIVE_API}/files/${fileId}?alt=media`);
    return res.text();
}

/** Apaga um arquivo (rotação). */
export async function deleteBackup(token: string, fileId: string): Promise<void> {
    await driveFetch(token, `${DRIVE_API}/files/${fileId}`, { method: 'DELETE' });
}

/** Mantém só os `keep` backups mais recentes; apaga o resto (best-effort). */
export async function rotateBackups(token: string, folderId: string, keep: number): Promise<void> {
    const files = await listBackups(token, folderId);
    for (const file of files.slice(Math.max(0, keep))) {
        try {
            await deleteBackup(token, file.id);
        } catch {
            // se um arquivo não puder ser apagado, segue a vida (não falha o backup)
        }
    }
}
